import type { DocNode } from '@atlaskit/adf-schema';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

function filterValidAdfNodes(nodes: (ADFEntity | null | undefined)[]): ADFEntity[] {
	return nodes.filter((node): node is ADFEntity => node !== undefined && node !== null);
}

export const parseSnippetContent = (content: string): ADFEntity | ADFEntity[] | undefined => {
	try {
		const parsedContent = JSON.parse(content);

		// Handle fragment (array of nodes without doc wrapper)
		if (Array.isArray(parsedContent)) {
			return filterValidAdfNodes(parsedContent);
		}

		// Handle single node or doc node
		const adfContent = parsedContent as ADFEntity;

		if (adfContent.type === 'doc' && Array.isArray(adfContent.content)) {
			return filterValidAdfNodes(adfContent.content);
		}

		return adfContent;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Error processing snippet ADF:', error);
		return undefined;
	}
};

const EMPTY_ADF = { type: 'doc' as const, version: 1 as const, content: [] };

/**
 * Recursively checks whether an ADF tree contains any node with the given type.
 */
export const adfContainsNodeType = (node: ADFEntity | DocNode, nodeType: string): boolean => {
	if (node.type === nodeType) {
		return true;
	}
	const children = (node as ADFEntity).content;
	if (Array.isArray(children)) {
		return children.some((child) => child != null && adfContainsNodeType(child, nodeType));
	}
	return false;
};

export const wrapAdfWithDocNode = (adf: ADFEntity | ADFEntity[] | undefined): DocNode => {
	if (!adf) {
		return EMPTY_ADF;
	}
	const docType = 'doc';
	const isArray = Array.isArray(adf);
	if (!isArray && adf.type === docType) {
		return adf as DocNode;
	}

	const content: any | any[] = isArray ? adf : [adf];
	return {
		version: 1,
		type: docType,
		content,
	};
};
